import { createSlice } from '@reduxjs/toolkit';
import {
  fetchQuizTopicsThunk, fetchQuizThunk, submitQuizThunk, fetchMySummaryThunk,
  adminFetchTopicsThunk, adminCreateTopicThunk, adminUpdateTopicThunk, adminDeleteTopicThunk,
  adminFetchQuestionsThunk, adminCreateQuestionThunk, adminUpdateQuestionThunk, adminDeleteQuestionThunk,
} from './credentialQuizActionThunks';

const initialState = {
  topics: [],              // user-facing, per-level completion included
  activeQuiz: null,        // { topicId, level, questions }
  lastResult: null,        // most recent QuizSubmitResult
  summary: null,           // KnowledgeSummary
  loading: false,
  error: null,

  // admin
  adminTopics: [],
  adminQuestionsByTopic: {},  // { [topicId]: Question[] }
  adminLoading: false,
  adminError: null,
};

const credentialQuizSlice = createSlice({
  name: 'credentialQuiz',
  initialState,
  reducers: {
    clearActiveQuiz: (state) => {
      state.activeQuiz = null;
      state.lastResult = null;
    },
    clearQuizError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // -- user-facing --
      .addCase(fetchQuizTopicsThunk.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchQuizTopicsThunk.fulfilled, (state, { payload }) => { state.loading = false; state.topics = payload; })
      .addCase(fetchQuizTopicsThunk.rejected, (state, { payload }) => { state.loading = false; state.error = payload; })

      .addCase(fetchQuizThunk.pending, (state) => { state.loading = true; state.error = null; state.activeQuiz = null; })
      .addCase(fetchQuizThunk.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.activeQuiz = { topicId: payload.topicId, level: payload.level, questions: payload.questions };
      })
      .addCase(fetchQuizThunk.rejected, (state, { payload }) => { state.loading = false; state.error = payload; })

      .addCase(submitQuizThunk.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(submitQuizThunk.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.lastResult = payload;
        const topic = state.topics.find((t) => t.id === payload.topic_id);
        if (topic) {
          topic.levels[payload.level] = {
            questions_correct: payload.questions_correct, questions_total: payload.questions_total,
            level_score: payload.level_score, attempt_number: payload.attempt_number,
          };
          topic.topic_score = Object.values(topic.levels).reduce((sum, l) => sum + (l?.level_score || 0), 0);
        }
      })
      .addCase(submitQuizThunk.rejected, (state, { payload }) => { state.loading = false; state.error = payload; })

      .addCase(fetchMySummaryThunk.fulfilled, (state, { payload }) => { state.summary = payload; })

      // -- admin --
      .addCase(adminFetchTopicsThunk.pending, (state) => { state.adminLoading = true; state.adminError = null; })
      .addCase(adminFetchTopicsThunk.fulfilled, (state, { payload }) => { state.adminLoading = false; state.adminTopics = payload; })
      .addCase(adminFetchTopicsThunk.rejected, (state, { payload }) => { state.adminLoading = false; state.adminError = payload; })

      .addCase(adminCreateTopicThunk.fulfilled, (state, { payload }) => { state.adminTopics.push(payload); })
      .addCase(adminCreateTopicThunk.rejected, (state, { payload }) => { state.adminError = payload; })

      .addCase(adminUpdateTopicThunk.fulfilled, (state, { payload }) => {
        const idx = state.adminTopics.findIndex((t) => t.id === payload.id);
        if (idx !== -1) state.adminTopics[idx] = payload;
      })
      .addCase(adminUpdateTopicThunk.rejected, (state, { payload }) => { state.adminError = payload; })

      .addCase(adminDeleteTopicThunk.fulfilled, (state, { payload: topicId }) => {
        state.adminTopics = state.adminTopics.filter((t) => t.id !== topicId);
        delete state.adminQuestionsByTopic[topicId];
      })
      .addCase(adminDeleteTopicThunk.rejected, (state, { payload }) => { state.adminError = payload; })

      .addCase(adminFetchQuestionsThunk.fulfilled, (state, { payload }) => {
        state.adminQuestionsByTopic[payload.topicId] = payload.questions;
      })
      .addCase(adminFetchQuestionsThunk.rejected, (state, { payload }) => { state.adminError = payload; })

      .addCase(adminCreateQuestionThunk.fulfilled, (state, { payload }) => {
        if (!state.adminQuestionsByTopic[payload.topicId]) state.adminQuestionsByTopic[payload.topicId] = [];
        state.adminQuestionsByTopic[payload.topicId].push(payload.question);
        const topic = state.adminTopics.find((t) => t.id === payload.topicId);
        if (topic) topic.question_count = (topic.question_count || 0) + 1;
      })
      .addCase(adminCreateQuestionThunk.rejected, (state, { payload }) => { state.adminError = payload; })

      .addCase(adminUpdateQuestionThunk.fulfilled, (state, { payload }) => {
        const list = state.adminQuestionsByTopic[payload.topic_id];
        if (list) {
          const idx = list.findIndex((q) => q.id === payload.id);
          if (idx !== -1) list[idx] = payload;
        }
      })
      .addCase(adminUpdateQuestionThunk.rejected, (state, { payload }) => { state.adminError = payload; })

      .addCase(adminDeleteQuestionThunk.fulfilled, (state, { payload }) => {
        const list = state.adminQuestionsByTopic[payload.topicId];
        if (list) state.adminQuestionsByTopic[payload.topicId] = list.filter((q) => q.id !== payload.questionId);
        const topic = state.adminTopics.find((t) => t.id === payload.topicId);
        if (topic) topic.question_count = Math.max(0, (topic.question_count || 1) - 1);
      })
      .addCase(adminDeleteQuestionThunk.rejected, (state, { payload }) => { state.adminError = payload; });
  },
});

export const { clearActiveQuiz, clearQuizError } = credentialQuizSlice.actions;

export const selectQuizTopics = (state) => state.credentialQuiz.topics;
export const selectActiveQuiz = (state) => state.credentialQuiz.activeQuiz;
export const selectLastQuizResult = (state) => state.credentialQuiz.lastResult;
export const selectKnowledgeSummary = (state) => state.credentialQuiz.summary;
export const selectQuizLoading = (state) => state.credentialQuiz.loading;
export const selectQuizError = (state) => state.credentialQuiz.error;

export const selectAdminQuizTopics = (state) => state.credentialQuiz.adminTopics;
export const selectAdminQuestionsForTopic = (state, topicId) => state.credentialQuiz.adminQuestionsByTopic[topicId] || [];
export const selectAdminQuizLoading = (state) => state.credentialQuiz.adminLoading;
export const selectAdminQuizError = (state) => state.credentialQuiz.adminError;

export default credentialQuizSlice.reducer;
